import React, { ForwardedRef, useCallback, useImperativeHandle } from "react";
import { unparse } from "papaparse";
import { dateToString } from "@src/utils/date";
import { Button } from "@src/components/elements";
import { generateDownloadLink } from "@src/utils/blob";
import { DownloadIcon } from "@src/components/elements/assets/DownloadIcon";

export interface CsvMap {
  [key: string]: string | number | null | undefined;
}

interface ExportDataProps {
  data: () =>
    | CsvMap[]
    | {
        data: CsvMap[];
        columns: string[];
      };
  name: string;
  transformColumn?: (
    column: string,
    value?: string | number | null,
  ) => [string, string | number | null | undefined];
  className?: string;
  buttonText?: string;
  style?: React.CSSProperties;
  ref?: ForwardedRef<ExportCsvDataTrigger>;
  buttonSize?: "small" | "medium" | "large";
}

export type ExportCsvDataTrigger = { download: () => void };

export const ExportCsvData: React.FC<ExportDataProps> = React.forwardRef(
  (
    { data, name, transformColumn, className, buttonText, style, buttonSize },
    ref: ForwardedRef<ExportCsvDataTrigger>,
  ) => {
    useImperativeHandle(ref, () => ({
      download() {
        return onClickExport();
      },
    }));

    const onClickExport = useCallback(() => {
      const csvData = data();
      // Map the csv data to the new columns, if available
      let csv;
      if (transformColumn) {
        if ("columns" in csvData) {
          const remapped = csvData.data.map((d) => {
            const newD: CsvMap = {};
            for (const [k, v] of Object.entries(d)) {
              const transformed = transformColumn(k, v);
              newD[transformed[0]] = transformed[1];
            }
            return newD;
          });

          csv = unparse(remapped, { columns: csvData.columns });
        } else {
          csv = unparse(csvData);
        }
      } else {
        if ("columns" in csvData) {
          csv = unparse(csvData.data, { columns: csvData.columns });
        } else {
          csv = unparse(csvData);
        }
      }
      generateDownloadLink(
        new Blob([csv], { type: "text/csv;charset=utf-8;" }),
        `Sudozi_export_${name}_${dateToString(new Date())}`,
        true,
      );
    }, [data, transformColumn, name]);

    return (
      <Button
        variant="tertiary"
        className={className}
        onClick={onClickExport}
        style={style}
        size={buttonSize}
      >
        {buttonText || "Export"}
        <DownloadIcon />
      </Button>
    );
  },
);

ExportCsvData.displayName = "ExportCsvData";
